/**
 * Connector config renderer component.
 * Conditionally renders specialized connector configuration components based on connector type.
 */

// React Imports
import { DataValidationConnectorConfig } from './DataValidationConnectorConfig';
import { EmailConnectorConfig } from './EmailConnectorConfig';
import { TaskManagementConnectorConfig } from './TaskManagementConnectorConfig';
import { BlobStorageConnectorConfig } from './BlobStorageConnectorConfig';
import { FhirConnectorConfig } from './FhirConnectorConfig';
import { OcrConnectorConfig } from './OcrConnectorConfig';
import { RagConnectorConfig } from './RagConnectorConfig';
import { SettingsForm } from './SettingsForm';
import type { ConnectorDetailDto } from '../../types/connector';

export interface ConnectorConfigRendererProps {
  connector: ConnectorDetailDto;
  instanceId?: string;
  settingsJson?: string;
  isConnected?: boolean;
  level?: 'System' | 'Tenant' | 'Project' | 'Instance';
  onChange?: (settingsJson: string) => void;
  onConnected?: () => void;
}

/**
 * Connector config renderer component.
 * Renders specialized configuration UI for specific connector types, or falls back to generic settings form.
 */
export const ConnectorConfigRenderer = ({
  connector,
  instanceId,
  settingsJson = '{}',
  isConnected = false,
  level = 'Instance',
  onChange,
  onConnected,
}: ConnectorConfigRendererProps) => {
  const connectorKey = connector.key.toLowerCase();

  // Render specialized config based on connector key
  if (connectorKey === 'data_validation' || connectorKey.includes('data-validation')) {
    return (
      <DataValidationConnectorConfig
        settingsJson={settingsJson}
        onChange={onChange}
      />
    );
  }

  if (connectorKey === 'email' || connectorKey.includes('email')) {
    return (
      <EmailConnectorConfig
        instanceId={instanceId}
        settingsJson={settingsJson}
        isConnected={isConnected}
        onChange={onChange}
        onConnected={onConnected}
      />
    );
  }

  if (connectorKey === 'task_management' || connectorKey.includes('task-management') || connectorKey.includes('taskmanagement')) {
    return (
      <TaskManagementConnectorConfig
        settingsJson={settingsJson}
        onChange={onChange}
      />
    );
  }

  if (connectorKey === 'blob_storage' || connectorKey.includes('blob-storage') || connectorKey.includes('blobstorage')) {
    return (
      <BlobStorageConnectorConfig
        settingsJson={settingsJson}
        onChange={onChange}
      />
    );
  }

  if (connectorKey === 'fhir' || connectorKey.includes('fhir')) {
    return (
      <FhirConnectorConfig
        instanceId={instanceId}
        settingsJson={settingsJson}
        isConnected={isConnected}
        onChange={onChange}
        onConnected={onConnected}
      />
    );
  }

  if (connectorKey === 'ocr' || connectorKey.includes('ocr')) {
    return (
      <OcrConnectorConfig
        settingsJson={settingsJson}
        onChange={onChange}
      />
    );
  }

  if (connectorKey === 'rag' || connectorKey.includes('rag') || connectorKey.includes('ragflow')) {
    return (
      <RagConnectorConfig
        settingsJson={settingsJson}
        onChange={onChange}
      />
    );
  }

  // Fallback to generic settings form for connectors without specialized config
  return (
    <SettingsForm
      settingsJson={settingsJson}
      level={level}
      onChange={onChange}
    />
  );
};
